/** Список наборов и создание нового. */

import { useState, type ReactNode } from 'react';
import {
  EVENT_RESULT_LABEL,
  hasCapability,
  mapsForEventType,
  type EventStatus,
  type RosterEvent,
  type SessionUser,
} from '@ak/shared';

import { apiPost } from '../lib/api.ts';
import { formatDateTime, formatRelative } from '../lib/format.ts';
import { navigate, useLinkHandler } from '../lib/router.ts';
import { useResource } from '../lib/useResource.ts';
import { useToast } from '../components/toast.tsx';
import { TimePicker } from '../components/time-picker.tsx';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  PageHeader,
  Panel,
  SegmentedControl,
  Select,
  Skeleton,
  TextInput,
  type SegmentedOption,
} from '../components/ui.tsx';

interface EventRow extends RosterEvent {
  plusCount: number;
  reserveCount: number;
}

const STATUS_LABEL: Record<EventStatus, string> = {
  open: 'Открыт',
  closed: 'Закрыт',
  cancelled: 'Отменён',
};

const STATUS_TONE: Record<EventStatus, 'positive' | 'neutral' | 'danger'> = {
  open: 'positive',
  closed: 'neutral',
  cancelled: 'danger',
};

/** Отбор по итогу мероприятия. Пустое значение — без отбора. */
type ResultFilter = '' | 'win' | 'loss' | 'afk';

const FILTERS: SegmentedOption<ResultFilter>[] = [
  { value: '', label: 'Все' },
  { value: 'win', label: 'Победы', color: 'var(--positive)' },
  { value: 'loss', label: 'Поражения', color: 'var(--danger)' },
  { value: 'afk', label: 'АФК', color: 'var(--text-muted)' },
];

/** Цвет значка итога: АФК нейтральный серый. */
const RESULT_TONE = { win: 'positive', loss: 'danger', afk: 'neutral' } as const;

const EMPTY_TITLE: Record<Exclude<ResultFilter, ''>, string> = {
  win: 'Побед пока нет',
  loss: 'Поражений пока нет',
  afk: 'АФК пока нет',
};

function CreateEventModal({
  eventTypes,
  defaultCapacity,
  timezone,
  onClose,
}: {
  eventTypes: string[];
  defaultCapacity: number;
  timezone: string;
  onClose: () => void;
}): ReactNode {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [eventType, setEventType] = useState(eventTypes[0] ?? 'MCL');
  const [startsAt, setStartsAt] = useState('');
  const [capacity, setCapacity] = useState(String(defaultCapacity));
  const [mapId, setMapId] = useState('');

  // Карты зависят от типа: на MCL и VZZ они разные, у капта их нет вовсе.
  const maps = mapsForEventType(eventType);
  const mapMissing = maps.length > 0 && mapId === '';

  const submit = (): void => {
    setBusy(true);
    apiPost<{ event: RosterEvent }>('/api/events', {
      eventType,
      startsAt,
      capacity: Number.parseInt(capacity, 10),
      mapId: mapId === '' ? null : mapId,
    })
      .then((result) => {
        toast.success(`Набор #${String(result.event.id)} создан и опубликован в Discord`);
        onClose();
        navigate(`/events/${String(result.event.id)}`);
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось создать набор');
        setBusy(false);
      });
  };

  return (
    <Modal
      title="Создать набор"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          {/* Без времени сервер набор не создаст: кнопка не должна
              обещать того, чем закончится отказ. */}
          <Button variant="primary" onClick={submit} busy={busy} disabled={startsAt === '' || mapMissing}>
            Создать и опубликовать
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Тип мероприятия">
          <Select
            value={eventType}
            onChange={(event) => {
              setEventType(event.target.value);
              // Карта прежнего типа новому не подходит: на VZZ не играют
              // в Ghetto, и оставленный выбор был бы ошибкой.
              setMapId('');
            }}
          >
            {eventTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Время проведения" hint={`Часовой пояс сообщества: ${timezone}`}>
          <TimePicker value={startsAt} timezone={timezone} onChange={setStartsAt} />
        </Field>

        <Field label="Размер основного состава">
          <TextInput
            type="number"
            min={1}
            max={100}
            value={capacity}
            onChange={(event) => {
              setCapacity(event.target.value);
            }}
          />
        </Field>

        {maps.length > 0 && (
          <Field label="Карта" hint="Без карты набор не создать">
            <Select
              value={mapId}
              onChange={(event) => {
                setMapId(event.target.value);
              }}
            >
              <option value="">Выберите карту</option>
              {maps.map((map) => (
                <option key={map.id} value={map.id}>
                  {map.label}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          После создания бот опубликует набор с упоминанием @everyone: MCL и VZZ — в канале плюсов
          MCL, остальное — в общем канале плюсов.
        </p>
      </div>
    </Modal>
  );
}

export function EventsPage({
  user,
  timezone,
  eventTypes,
  defaultCapacity,
}: {
  user: SessionUser;
  timezone: string;
  eventTypes: string[];
  defaultCapacity: number;
}): ReactNode {
  const [filter, setFilter] = useState<ResultFilter>('');
  const [creating, setCreating] = useState(false);
  const path = filter === '' ? '/api/events' : `/api/events?result=${filter}`;
  const { data, loading, error, reload } = useResource<{ events: EventRow[] }>(path);
  const onLinkClick = useLinkHandler();

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  return (
    <>
      <PageHeader
        title="Наборы"
        actions={
          <>
            <SegmentedControl label="Отбор по итогу" options={FILTERS} value={filter} onChange={setFilter} />
            {hasCapability(user.level, 'events.create') && (
              <Button
                variant="primary"
                onClick={() => {
                  setCreating(true);
                }}
              >
                Создать набор
              </Button>
            )}
          </>
        }
      />

      {loading || data === null ? (
        <Panel>
          <Skeleton rows={6} />
        </Panel>
      ) : data.events.length === 0 ? (
        <Panel>
          {filter === '' ? (
            <EmptyState title="Наборов нет" description="Создайте первый набор — бот опубликует его в Discord." />
          ) : (
            <EmptyState
              title={EMPTY_TITLE[filter]}
              description="Сюда попадают наборы с отмеченным итогом. Итог ставится на странице набора после его закрытия."
            />
          )}
        </Panel>
      ) : (
        <div className="grid gap-3">
          {data.events.map((event) => {
            const filled = event.capacity === 0 ? 0 : Math.min(1, event.plusCount / event.capacity);

            return (
              <a key={event.id} href={`/events/${String(event.id)}`} onClick={onLinkClick} className="block">
                <Panel className="p-4 transition-colors hover:brightness-105">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{event.eventType}</span>
                        <Badge tone={STATUS_TONE[event.status]}>{STATUS_LABEL[event.status]}</Badge>
                        {event.result !== null && (
                          <Badge tone={RESULT_TONE[event.result]} dot>
                            {EVENT_RESULT_LABEL[event.result]}
                          </Badge>
                        )}
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          #{event.id}
                        </span>
                      </div>
                      <div className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {formatDateTime(event.startsAt, timezone)}
                        <span style={{ color: 'var(--text-muted)' }}> · {formatRelative(event.startsAt)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-sm font-medium tabular-nums">
                          {event.plusCount}/{event.capacity}
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          +{event.reserveCount} в замене
                        </div>
                      </div>

                      <div className="h-2 w-28 overflow-hidden rounded-full" style={{ background: 'var(--surface-hover)' }}>
                        <div
                          className="h-full rounded-full transition-[width]"
                          style={{ width: `${String(filled * 100)}%`, background: 'var(--accent)' }}
                        />
                      </div>
                    </div>
                  </div>
                </Panel>
              </a>
            );
          })}
        </div>
      )}

      {creating && (
        <CreateEventModal
          eventTypes={eventTypes}
          defaultCapacity={defaultCapacity}
          timezone={timezone}
          onClose={() => {
            setCreating(false);
            reload();
          }}
        />
      )}
    </>
  );
}
